/**
 * ---------------------------------------------------------------------------------------------------------------------
 * NUBELLE 4-Way Bra — product page behaviour
 *
 * Scope:  only loaded by the "nubelle-*" sections used on templates/product.nubelle-nathloser-4-way-bh.json
 * Deps:   none. Everything is progressive enhancement — without JS every section still renders complete content.
 * ---------------------------------------------------------------------------------------------------------------------
 */

(function () {
  'use strict';

  var PREFERS_REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* -------------------------------------------------------------------------------------------------------------------
     Reveal on scroll
     Elements marked with [data-nbl-reveal] fade in once. `data-nbl-reveal` may hold a delay in ms.
     ---------------------------------------------------------------------------------------------------------------- */

  var revealObserver = null;

  function getRevealObserver() {
    if (revealObserver || !('IntersectionObserver' in window)) {
      return revealObserver;
    }

    revealObserver = new IntersectionObserver(
      function (entries, observer) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;

          var delay = parseInt(entry.target.dataset.nblReveal, 10);
          entry.target.style.transitionDelay = delay > 0 ? delay + 'ms' : '';
          entry.target.classList.add('is-revealed');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.1 }
    );

    return revealObserver;
  }

  function initReveal(root) {
    var elements = (root || document).querySelectorAll('[data-nbl-reveal]:not(.is-revealed)');
    var observer = getRevealObserver();

    elements.forEach(function (element) {
      if (!observer || PREFERS_REDUCED_MOTION.matches) {
        element.classList.add('is-revealed');
      } else {
        observer.observe(element);
      }
    });
  }

  /* -------------------------------------------------------------------------------------------------------------------
     <nbl-wearing-styles> — tab list that swaps the stage image for each strap style
     ---------------------------------------------------------------------------------------------------------------- */

  var WearingStyles = function () {
    return Reflect.construct(HTMLElement, [], WearingStyles);
  };

  WearingStyles.prototype = Object.create(HTMLElement.prototype);
  WearingStyles.prototype.constructor = WearingStyles;
  Object.setPrototypeOf(WearingStyles, HTMLElement);

  WearingStyles.prototype.connectedCallback = function () {
    this.options = Array.prototype.slice.call(this.querySelectorAll('[data-nbl-style-option]'));
    this.slides = Array.prototype.slice.call(this.querySelectorAll('[data-nbl-style-slide]'));

    if (this.options.length === 0) return;

    this.onClick = this.select.bind(this);
    this.onKeydown = this.handleKeydown.bind(this);

    this.options.forEach(
      function (option) {
        option.addEventListener('click', this.onClick);
        option.addEventListener('keydown', this.onKeydown);
      }.bind(this)
    );
  };

  WearingStyles.prototype.disconnectedCallback = function () {
    (this.options || []).forEach(
      function (option) {
        option.removeEventListener('click', this.onClick);
        option.removeEventListener('keydown', this.onKeydown);
      }.bind(this)
    );
  };

  WearingStyles.prototype.select = function (event) {
    var option = event.currentTarget;
    var index = this.options.indexOf(option);

    if (index === -1) return;

    this.options.forEach(function (item, itemIndex) {
      item.setAttribute('aria-selected', itemIndex === index ? 'true' : 'false');
      item.setAttribute('tabindex', itemIndex === index ? '0' : '-1');
    });

    this.slides.forEach(function (slide, slideIndex) {
      slide.classList.toggle('is-active', slideIndex === index);
      slide.toggleAttribute('aria-hidden', slideIndex !== index);
    });
  };

  /** Roving tabindex so the selector behaves like a real tab list. */
  WearingStyles.prototype.handleKeydown = function (event) {
    var index = this.options.indexOf(event.currentTarget);
    var next = null;

    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        next = (index + 1) % this.options.length;
        break;

      case 'ArrowLeft':
      case 'ArrowUp':
        next = (index - 1 + this.options.length) % this.options.length;
        break;

      case 'Home':
        next = 0;
        break;

      case 'End':
        next = this.options.length - 1;
        break;

      default:
        return;
    }

    event.preventDefault();
    this.options[next].focus();
    this.options[next].click();
  };

  /* -------------------------------------------------------------------------------------------------------------------
     <nbl-before-after> — draggable comparison slider, backed by a real range input for keyboard/AT support
     ---------------------------------------------------------------------------------------------------------------- */

  var BeforeAfter = function () {
    return Reflect.construct(HTMLElement, [], BeforeAfter);
  };

  BeforeAfter.prototype = Object.create(HTMLElement.prototype);
  BeforeAfter.prototype.constructor = BeforeAfter;
  Object.setPrototypeOf(BeforeAfter, HTMLElement);

  BeforeAfter.prototype.connectedCallback = function () {
    this.frame = this.querySelector('[data-nbl-ba-frame]');
    this.range = this.querySelector('[data-nbl-ba-range]');

    if (!this.frame || !this.range) return;

    this.onInput = this.update.bind(this);
    this.onPointerDown = this.startDrag.bind(this);
    this.onPointerMove = this.drag.bind(this);
    this.onPointerUp = this.endDrag.bind(this);

    this.range.addEventListener('input', this.onInput);
    this.frame.addEventListener('pointerdown', this.onPointerDown);

    this.update();
  };

  BeforeAfter.prototype.disconnectedCallback = function () {
    if (!this.range) return;

    this.range.removeEventListener('input', this.onInput);
    this.frame.removeEventListener('pointerdown', this.onPointerDown);
    window.removeEventListener('pointermove', this.onPointerMove);
    window.removeEventListener('pointerup', this.onPointerUp);
  };

  BeforeAfter.prototype.update = function () {
    this.frame.style.setProperty('--nbl-ba-position', this.range.value + '%');
  };

  BeforeAfter.prototype.startDrag = function (event) {
    this.isDragging = true;
    window.addEventListener('pointermove', this.onPointerMove);
    window.addEventListener('pointerup', this.onPointerUp);
    this.drag(event);
  };

  BeforeAfter.prototype.drag = function (event) {
    if (!this.isDragging) return;

    var bounds = this.frame.getBoundingClientRect();
    var ratio = ((event.clientX - bounds.left) / bounds.width) * 100;

    this.range.value = Math.min(100, Math.max(0, Math.round(ratio)));
    this.update();
  };

  BeforeAfter.prototype.endDrag = function () {
    this.isDragging = false;
    window.removeEventListener('pointermove', this.onPointerMove);
    window.removeEventListener('pointerup', this.onPointerUp);
  };

  /* -------------------------------------------------------------------------------------------------------------------
     <nbl-reels> — snap scroller of 9:16 clips.
     Only the reels currently on screen play, and they play muted until the visitor asks for sound.
     ---------------------------------------------------------------------------------------------------------------- */

  var Reels = function () {
    return Reflect.construct(HTMLElement, [], Reels);
  };

  Reels.prototype = Object.create(HTMLElement.prototype);
  Reels.prototype.constructor = Reels;
  Object.setPrototypeOf(Reels, HTMLElement);

  Reels.prototype.connectedCallback = function () {
    this.track = this.querySelector('[data-nbl-reels-track]');
    this.videos = Array.prototype.slice.call(this.querySelectorAll('video'));

    if (this.track) {
      var prev = this.querySelector('[data-nbl-reels-prev]');
      var next = this.querySelector('[data-nbl-reels-next]');

      this.onPrev = this.scrollByCard.bind(this, -1);
      this.onNext = this.scrollByCard.bind(this, 1);
      this.onScroll = this.syncArrows.bind(this);

      if (prev) prev.addEventListener('click', this.onPrev);
      if (next) next.addEventListener('click', this.onNext);
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      this.syncArrows();
    }

    this.onSoundClick = this.toggleSound.bind(this);
    this.querySelectorAll('[data-nbl-reel-sound]').forEach(
      function (button) {
        button.addEventListener('click', this.onSoundClick);
      }.bind(this)
    );

    if (this.videos.length === 0 || !('IntersectionObserver' in window)) return;

    // Autoplay is deliberately visibility-driven: several <video> elements playing at once is a real bandwidth cost.
    this.playbackObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          var video = entry.target;

          if (entry.isIntersecting && !PREFERS_REDUCED_MOTION.matches) {
            var attempt = video.play();
            if (attempt && attempt.catch) attempt.catch(function () {});
          } else {
            video.pause();
          }
        });
      },
      { threshold: 0.5 }
    );

    this.videos.forEach(
      function (video) {
        video.muted = true;
        video.setAttribute('playsinline', '');
        this.playbackObserver.observe(video);
      }.bind(this)
    );
  };

  Reels.prototype.disconnectedCallback = function () {
    if (this.playbackObserver) this.playbackObserver.disconnect();
    if (this.track) this.track.removeEventListener('scroll', this.onScroll);
  };

  Reels.prototype.scrollByCard = function (direction) {
    var card = this.track.querySelector('.nbl-reel');
    if (!card) return;

    var step = card.getBoundingClientRect().width + 16;
    this.track.scrollBy({ left: step * direction, behavior: 'smooth' });
  };

  Reels.prototype.syncArrows = function () {
    var prev = this.querySelector('[data-nbl-reels-prev]');
    var next = this.querySelector('[data-nbl-reels-next]');
    var max = this.track.scrollWidth - this.track.clientWidth;

    if (prev) prev.disabled = this.track.scrollLeft <= 1;
    if (next) next.disabled = this.track.scrollLeft >= max - 1;
  };

  /** Sound is exclusive: unmuting one reel mutes every other one. */
  Reels.prototype.toggleSound = function (event) {
    var button = event.currentTarget;
    var reel = button.closest('.nbl-reel');
    var video = reel && reel.querySelector('video');

    if (!video) return;

    var unmute = video.muted;

    this.videos.forEach(function (item) {
      item.muted = true;
    });

    this.querySelectorAll('[data-nbl-reel-sound]').forEach(function (item) {
      item.setAttribute('aria-pressed', 'false');
    });

    if (unmute) {
      video.muted = false;
      button.setAttribute('aria-pressed', 'true');
      var attempt = video.play();
      if (attempt && attempt.catch) attempt.catch(function () {});
    }
  };

  /* -------------------------------------------------------------------------------------------------------------------
     Boot
     ---------------------------------------------------------------------------------------------------------------- */

  if (!window.customElements.get('nbl-wearing-styles')) {
    window.customElements.define('nbl-wearing-styles', WearingStyles);
  }

  if (!window.customElements.get('nbl-before-after')) {
    window.customElements.define('nbl-before-after', BeforeAfter);
  }

  if (!window.customElements.get('nbl-reels')) {
    window.customElements.define('nbl-reels', Reels);
  }

  initReveal();

  // Re-scan when a section is (re)rendered inside the theme editor.
  document.addEventListener('shopify:section:load', function (event) {
    initReveal(event.target);
  });
})();
